/**
 * Finding Common Purpose — pilot deployment config.
 *
 * Geography: Routt County, CO (FIPS state 08, county 107)
 *
 * Indicators drawn from ACS 5-year estimates and CDC Places.
 * Years array excludes 2020 (ACS 5-year not released for 2020 due to COVID disruption).
 */

const fcpPilotConfig = {
  org: {
    name: "Finding Common Purpose",
    geography: {
      level: "tract",
      state: "08",
      county: "107",
      label: "Routt County, CO"
    }
  },
  map: {
    center: [-106.8317, 40.4850],
    zoom: 10,
    style: "mapbox://styles/mapbox/light-v11"
  },
  indicators: [
    {
      id: "poverty_rate",
      label: "Poverty Rate",
      source: "census",
      censusTable: "B17001",
      numeratorVar: "B17001_002E",
      denominatorVar: "B17001_001E",
      unit: "percent",
      rationale: "Share of residents living below the federal poverty line.",
      dataSource: "U.S. Census Bureau, ACS 5-Year Estimates (B17001)",
      years: [2018, 2019, 2021, 2022, 2023, 2024]
    },
    {
      id: "median_household_income",
      label: "Median Household Income",
      source: "census",
      censusTable: "B19013",
      // Median is a direct estimate — no ratio needed; denominatorVar omitted
      numeratorVar: "B19013_001E",
      denominatorVar: null,
      unit: "dollars",
      rationale: "Median household income tracks overall economic wellbeing.",
      dataSource: "U.S. Census Bureau, ACS 5-Year Estimates (B19013)",
      years: [2018, 2019, 2021, 2022, 2023, 2024]
    },
    {
      id: "no_health_insurance",
      label: "Uninsured Rate",
      source: "census",
      // B27003: health insurance coverage by sex — simpler than B27001
      // B27003_004E = male uninsured, B27003_007E = female uninsured, B27003_001E = total
      censusTable: "B27003",
      numeratorVar: "B27003_004E+B27003_007E",
      denominatorVar: "B27003_001E",
      unit: "percent",
      rationale: "Share of residents without health insurance coverage.",
      dataSource: "U.S. Census Bureau, ACS 5-Year Estimates (B27003)",
      years: [2018, 2019, 2021, 2022, 2023, 2024]
    },
    {
      id: "bachelor_or_higher",
      label: "Bachelor's Degree or Higher",
      source: "census",
      censusTable: "B15003",
      // B15003_022E = bachelor's, _023E = master's, _024E = professional, _025E = doctorate
      numeratorVar: "B15003_022E+B15003_023E+B15003_024E+B15003_025E",
      denominatorVar: "B15003_001E",
      unit: "percent",
      rationale: "Share of residents 25+ with a bachelor's degree or higher.",
      dataSource: "U.S. Census Bureau, ACS 5-Year Estimates (B15003)",
      years: [2018, 2019, 2021, 2022, 2023, 2024]
    },
    // Employment
    { id: "unemployment_rate",        source: "census", censusTable: "B23025", numeratorVar: "B23025_005E",       denominatorVar: "B23025_002E",  unit: "percent",  years: [2018, 2019, 2021, 2022, 2023, 2024] },
    { id: "labor_force_participation", source: "census", censusTable: "B23025", numeratorVar: "B23025_002E",       denominatorVar: "B23025_001E",  unit: "percent",  years: [2018, 2019, 2021, 2022, 2023, 2024] },
    // Housing
    { id: "owner_occupied",     source: "census", censusTable: "B25003", numeratorVar: "B25003_002E",       denominatorVar: "B25003_001E",  unit: "percent",  years: [2018, 2019, 2021, 2022, 2023, 2024] },
    { id: "median_home_value",  source: "census", censusTable: "B25077", numeratorVar: "B25077_001E",       denominatorVar: null,           unit: "dollars",  years: [2018, 2019, 2021, 2022, 2023, 2024] },
    { id: "housing_cost_burden", source: "census", censusTable: "B25070", numeratorVar: "B25070_007E+B25070_008E+B25070_009E+B25070_010E", denominatorVar: "B25070_001E", unit: "percent", years: [2018, 2019, 2021, 2022, 2023, 2024] },
    { id: "vacant_housing",     source: "census", censusTable: "B25002", numeratorVar: "B25002_003E",       denominatorVar: "B25002_001E",  unit: "percent",  years: [2018, 2019, 2021, 2022, 2023, 2024] },
    // Health (CDC PLACES)
    { id: "obesity",         source: "cdc", cdcViewId: "swc5-untb", cdcMeasureId: "OBESITY",   unit: "percent", years: [2019, 2020, 2021, 2022, 2023, 2024] },
    { id: "diabetes",        source: "cdc", cdcViewId: "swc5-untb", cdcMeasureId: "DIABETES",  unit: "percent", years: [2019, 2020, 2021, 2022, 2023, 2024] },
    { id: "mental_health",   source: "cdc", cdcViewId: "swc5-untb", cdcMeasureId: "MHLTH",     unit: "percent", years: [2019, 2020, 2021, 2022, 2023, 2024] },
    { id: "no_physical_activity", source: "cdc", cdcViewId: "swc5-untb", cdcMeasureId: "LPA",  unit: "percent", years: [2019, 2020, 2021, 2022, 2023, 2024] },
    // Demographics
    { id: "median_age",              source: "census", censusTable: "B01002", numeratorVar: "B01002_001E", denominatorVar: null,          unit: "count",   years: [2018, 2019, 2021, 2022, 2023, 2024] },
    { id: "hispanic_or_latino",      source: "census", censusTable: "B03003", numeratorVar: "B03003_003E", denominatorVar: "B03003_001E", unit: "percent", years: [2018, 2019, 2021, 2022, 2023, 2024] },
    { id: "black_or_african_american", source: "census", censusTable: "B02001", numeratorVar: "B02001_003E", denominatorVar: "B02001_001E", unit: "percent", years: [2018, 2019, 2021, 2022, 2023, 2024] }
  ],
  // IDs used to initialize selectedIndicators on first load (before any localStorage state).
  // Must match `id` values in indicatorRegistry.js.
  defaultIndicatorIds: [
    'poverty_rate', 'median_household_income', 'no_health_insurance', 'bachelor_or_higher', 'mental_health'
  ],

  // Increment configVersion whenever you want all browsers to reset to defaultSettings.
  // Use the "Export Config" button in the admin panel to generate updated values.
  // v4: reduced to 5 core indicators with themes, goals, gradients, and rationales
  configVersion: 4,
  defaultSettings: {
    indicatorIds: [
      'poverty_rate', 'median_household_income', 'no_health_insurance', 'bachelor_or_higher', 'mental_health'
    ],
    enabledIds: [
      'poverty_rate', 'median_household_income', 'no_health_insurance', 'bachelor_or_higher', 'mental_health'
    ],
    goals: {
      poverty_rate: 4,
      no_health_insurance: 20,
      bachelor_or_higher: 60,
      mental_health: 20
    },
    themes: [
      { label: 'Health',     indicatorIds: ['no_health_insurance', 'mental_health'] },
      { label: 'Wealth',     indicatorIds: ['poverty_rate', 'median_household_income'] },
      { label: 'Education',  indicatorIds: ['bachelor_or_higher'] }
    ],
    gradients: {
      poverty_rate:             'red',
      median_household_income:  'green',
      no_health_insurance:      'purple',
      bachelor_or_higher:       'teal',
      mental_health:            'blue'
    },
    rationales: {
      poverty_rate:             'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
      median_household_income:  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
      no_health_insurance:      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
      bachelor_or_higher:       'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
      mental_health:            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.'
    },
    yearRange: { from: null, to: null }
  },
  branding: {
    primaryColor: "#2d6b49",
    accentColor: "#c8614a",
    backgroundColor: "#f0fbff",
    fontHeading: "Playfair Display",
    fontBody: "Inter"
  }
};

export default fcpPilotConfig;
